import * as React from 'react';
import { DataGroupHeader } from '../../../../components/data-group/DataGroupHeader';
import { allPermissions } from '../../../../models/enums/permission-groups';
import { PermissionRole, PermissionRoles, roleOf } from '../../../../models/enums/permission-roles';
import { PermissionDetails, Permissions } from '../../../../models/enums/permissions';
import { ServiceCenter } from '../../../../models/utilities/service-center';
import { PresetRoleBox } from './PresetRoleBox';

export interface UserPermissionsByServiceCenter {
    [serviceCenter: string]: Permissions[];
}

interface IProps {
    /** What the user holds, by service center. ALL is every service center. */
    permissions: UserPermissionsByServiceCenter;
    /** The service centers a user can be given permissions in */
    serviceCenters: ServiceCenter[];
    onChange: (permissions: UserPermissionsByServiceCenter) => void;
}

/** Where a permission is in the menu: "Shipping" for "Shipping > Trucks > View" */
const sectionOf = (permission: Permissions) => PermissionDetails[permission].display.split(' > ')[0];

/** What is left of how a permission is shown once its section is taken off: "Trucks > View" */
const labelOf = (permission: Permissions) => {
    const parts = PermissionDetails[permission].display.split(' > ');
    return parts.length > 1 ? parts.slice(1).join(' > ') : parts[0];
};

const sections = (): Array<{ name: string; permissions: Permissions[] }> => {
    const grouped: { [section: string]: Permissions[] } = {};
    allPermissions()
        .sort((a, b) => a - b)
        .forEach(x => (grouped[sectionOf(x)] = grouped[sectionOf(x)] || []).push(x));

    return Object.keys(grouped).map(name => ({ name, permissions: grouped[name] }));
};

/**
 * What a user may do, in each service center they work in: a role to start from, and the permissions to add or take away.
 */
export const PermissionMatrix = (props: IProps) => {

    const { permissions, serviceCenters, onChange } = props;
    const [open, setOpen] = React.useState<{ [serviceCenter: string]: boolean }>({});

    const configured = Object.keys(permissions);
    const addable = serviceCenters.filter(x => configured.indexOf(x.name) < 0);

    const describe = (name: string) => {
        const found = serviceCenters.find(x => x.name === name);
        return found && found.description ? found.description : name;
    };

    const set = (serviceCenter: string, held: Permissions[]) => onChange({ ...permissions, [serviceCenter]: held });

    const toggle = (serviceCenter: string, permission: Permissions) => {
        const held = permissions[serviceCenter] || [];
        set(serviceCenter, held.indexOf(permission) >= 0 ? held.filter(x => x !== permission) : [...held, permission]);
    };

    const remove = (serviceCenter: string) => {
        const rest = { ...permissions };
        delete rest[serviceCenter];
        onChange(rest);
    };

    const useRole = (serviceCenter: string, role: PermissionRole) => set(serviceCenter, [...role.permissions]);

    const onAdd = (e: React.ChangeEvent<HTMLSelectElement>) => {
        if (e.target.value) {
            set(e.target.value, []);
            setOpen({ ...open, [e.target.value]: true });
        }
    };

    return <React.Fragment>

        <div className="row mt-3">
            <div className="col">
                <DataGroupHeader title="Service Centers &amp; Permissions" />
                <p>
                    A user is given permissions in each service center they work in. What is given for
                    &quot;All&quot; applies in every service center. Start a service center from one of the roles,
                    and then add or take away permissions.
                </p>
            </div>
        </div>

        {
            configured.length === 0
                ? <div className="row"><div className="col"><p className="callout">This user has no permissions in any service center.</p></div></div>
                : null
        }

        {
            configured.map(serviceCenter => {

                const held = permissions[serviceCenter] || [];
                const role = roleOf(held);

                return <div className="permission-service-center" key={ serviceCenter } data-testid={ `permissions-${ serviceCenter }` }>

                    <div className="row">
                        <div className="col">
                            <h3>
                                { describe(serviceCenter) }
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    onClick={ () => setOpen({ ...open, [serviceCenter]: !open[serviceCenter] }) }>
                                    { open[serviceCenter] ? 'Hide permissions' : `Show permissions (${ held.length })` }
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    onClick={ () => remove(serviceCenter) }>
                                    Remove
                                </button>
                            </h3>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col">
                            <div className="button-bar">
                                {
                                    PermissionRoles.map(r => (
                                        <PresetRoleBox
                                            key={ r.key }
                                            icon={ r.icon }
                                            title={ r.name }
                                            description={ r.description }
                                            onclick={ () => useRole(serviceCenter, r) }
                                            activeCheck={ role === r.key } />
                                    ))
                                }
                                <PresetRoleBox
                                    icon="la-star"
                                    title="None"
                                    description="Removes all permissions for this user in this service center."
                                    onclick={ () => set(serviceCenter, []) }
                                    activeCheck={ role === 'none' } />
                            </div>
                        </div>
                    </div>

                    {
                        open[serviceCenter]
                            ? <div className="row">
                                <div className="col">
                                    <div className="permission-sections">
                                        {
                                            sections().map(section => (
                                                <div className="permission-section" key={ section.name }>
                                                    <h4 className="permission-section-heading">{ section.name }</h4>
                                                    {
                                                        section.permissions.map(permission => (
                                                            <div className="permission-option" key={ permission }>
                                                                <label>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={ held.indexOf(permission) >= 0 }
                                                                        onChange={ () => toggle(serviceCenter, permission) } />
                                                                    { ' ' }{ labelOf(permission) }
                                                                </label>
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                            : null
                    }
                </div>;
            })
        }

        {
            addable.length > 0
                ? <div className="row mt-3">
                    <div className="col-md-4">
                        <label htmlFor="add-service-center">Add a service center</label>
                        <select id="add-service-center" className="form-control" value="" onChange={ onAdd }>
                            <option value="">Select a service center...</option>
                            {
                                addable.map(x => <option key={ x.id } value={ x.name }>{ x.description || x.name }</option>)
                            }
                        </select>
                    </div>
                </div>
                : null
        }

    </React.Fragment>;
};
